/**
 * Tiny runtime bridge for Privy.
 *
 * Privy hooks are only valid inside <PrivyProvider>, which main.tsx mounts only
 * when the backend exposes a Privy app id (see /config). Components that live
 * outside that guarantee — or that must stay provider-agnostic — read these
 * helpers instead of calling Privy hooks directly:
 *
 *  - `isPrivyReady()` lets the UI render the Privy-powered sign-in CTA only when
 *    the provider is actually in the tree.
 *  - `registerPrivyLogout()` / `runPrivyLogout()` expose Privy's imperative
 *    logout to non-provider code (e.g. the profile modal's sign-out handler).
 */

let privyReady = false;

type PrivyLogout = () => Promise<void> | void;
let privyLogoutFn: PrivyLogout | null = null;

/** Called by main.tsx right before it renders the PrivyProvider branch. */
export function markPrivyReady(): void {
  privyReady = true;
}

/** True iff a PrivyProvider is mounted — safe to render Privy-hook components. */
export function isPrivyReady(): boolean {
  return privyReady;
}

/** Registered by a provider-scoped bridge component (see main.tsx). */
export function registerPrivyLogout(fn: PrivyLogout): void {
  privyLogoutFn = fn;
}

/** Clears the Privy session if one is wired; no-op otherwise. */
export async function runPrivyLogout(): Promise<void> {
  if (privyLogoutFn) await privyLogoutFn();
}
